import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FiAlertCircle, FiCheck, FiLoader, FiMail, FiShield, FiUserCheck } from 'react-icons/fi';
import apiService from '../services/api';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.subscribeNewsletter({
        email: email.trim(),
        name: name.trim() || undefined,
        source: 'Website'
      });

      // Track subscription event
      await apiService.trackEvent({
        event: 'newsletter_signup',
        email: email.trim(),
        page: window.location.pathname
      });

      setSuccess(true);
      setEmail('');
      setName('');

      // Reset success message after 8 seconds
      setTimeout(() => setSuccess(false), 8000);
    } catch (err) {
      if (err.message.includes('already subscribed')) {
        setError('This email is already subscribed. Thank you for your interest!');
      } else {
        setError(err.message || 'Failed to subscribe. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-700 text-white py-16"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", bounce: 0.5 }}
            className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <FiCheck className="w-10 h-10 text-white" />
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold mb-4"
          >
            Thank You for Subscribing!
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-blue-100 mb-6"
          >
            You'll receive our latest insights, industry updates, and exclusive content directly in your inbox.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-center space-x-6 mb-6 text-blue-100"
          >
            <div className="flex items-center space-x-2">
              <FiShield className="w-5 h-5" />
              <span className="text-sm">Privacy Protected</span>
            </div>
            <div className="flex items-center space-x-2">
              <FiUserCheck className="w-5 h-5" />
              <span className="text-sm">No Spam Guarantee</span>
            </div>
          </motion.div>

          <motion.button
            onClick={() => setSuccess(false)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white/20 hover:bg-white/30 px-6 py-3 rounded-lg font-medium transition-all duration-300 border border-white/20"
          >
            Subscribe Another Email
          </motion.button>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-700 text-white py-16"
    >
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", bounce: 0.4 }}
          viewport={{ once: true }}
          className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <FiMail className="w-8 h-8 text-white" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
          className="text-3xl lg:text-4xl font-bold mb-4"
        >
          Stay Updated with Innofusion
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          viewport={{ once: true }}
          className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto"
        >
          Get the latest insights on technology trends, industry best practices, and exclusive updates delivered straight to your inbox.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          viewport={{ once: true }}
          className="flex items-center justify-center space-x-6 mb-8 text-blue-100"
        >
          <div className="flex items-center space-x-2">
            <FiShield className="w-4 h-4" />
            <span className="text-sm">Privacy Protected</span>
          </div>
          <div className="flex items-center space-x-2">
            <FiUserCheck className="w-4 h-4" />
            <span className="text-sm">Unsubscribe Anytime</span>
          </div>
          <div className="flex items-center space-x-2">
            <FiMail className="w-4 h-4" />
            <span className="text-sm">Quality Content</span>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          viewport={{ once: true }}
          className="max-w-md mx-auto space-y-4"
        >
          <div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent transition-all duration-300 backdrop-blur-sm"
            />
          </div>

          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clear error when user starts typing
                if (error) setError('');
              }}
              placeholder="Enter your email address"
              required
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent transition-all duration-300 backdrop-blur-sm"
            />
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center text-red-200 text-sm bg-red-500/20 px-3 py-2 rounded-lg border border-red-400/30"
            >
              <FiAlertCircle className="mr-2 flex-shrink-0" />
              {error}
            </motion.div>
          )}

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.05 }}
            whileTap={{ scale: loading ? 1 : 0.95 }}
            className="w-full bg-white text-blue-600 font-bold py-3 px-6 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <>
                <FiLoader className="animate-spin mr-2" />
                Subscribing...
              </>
            ) : (
              <>
                <FiMail className="mr-2" />
                Subscribe to Newsletter
              </>
            )}
          </motion.button>
        </motion.form>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          viewport={{ once: true }}
          className="text-sm text-blue-200 mt-4"
        >
          Join thousands of professionals staying ahead of technology trends. 
          We respect your privacy and never share your information.
        </motion.p>
      </div>
    </motion.section>
  );
};

export default NewsletterSection; 